import { createApiError } from '@/core/error-handling/ApiErrorHandler';
import type { MasterExportRequest, MasterImportResult, MasterPage, MasterQuery } from '@/features/masters';
import {
  EMPTY_SCOPE,
  HIERARCHY_LEVELS,
  HIERARCHY_LEVEL_LABEL,
  SCOPE_FIELD,
  getTemplateDetail,
  mockDescribeScope,
  mockHierarchyOptions,
  mockOfferedToCaller,
  mockPurchaseRequestCaller,
  mockRecordTemplateUse,
  mockResetTemplateUses,
  mockTemplateCategoryIds,
  mockTemplateScope,
  renderableSections,
  scopeApplies,
  scopeFromView,
  scopeOfQuery,
} from '@/features/templates';
import type { HierarchyScope } from '@/features/templates';
import type {
  SubmissionDecision,
  SubmissionDecisionResult,
  SubmissionHistoryEntry,
  PurchaseRequestSummary,
  PurchaseRequestSummaryCard,
  PurchaseRequestUsage,
  SubmissionStep,
  SubmittedValue,
  TemplateFormSubmissionDetail,
  TemplateFormSubmissionResult,
  TemplateFormSubmissionRow,
  TemplateFormSubmitRequest,
} from '../models/templateForm.types';
import { PR_LINE_REMARKS_MAX, isAwaitingMe, isDraftState, standardDecisionMessage } from '../models/templateForm.types';
import { isPurchaseRequestKind } from '../models/formsChannelKey';
import { mockLineLabels } from './purchaseCatalog.mock';

/**
 * The Purchase Request endpoints, behind `env.ENABLE_MOCKS`.
 *
 * The active listing and the detail come from the Templates feature's own mock
 * store, so a template picked here draws a real form. This answers the
 * submission calls with the server's rules: a submit that stores what it was
 * sent (with the same snapshots) and starts a two-step workflow
 * -- Manager Review, then Final Approval -- and decisions that move it exactly as
 * `SubmissionWorkflowEngine` does. The mock's signed-in user is every step's
 * approver, so the whole flow can be walked through by hand.
 */

export type MockSubmitOutcome =
  | { kind: 'ok' }
  | { kind: 'invalid'; errors: Array<{ field: string; message: string }> }
  | { kind: 'conflict' }
  | { kind: 'inactive' }
  | { kind: 'forbidden' };

const MOCK_USER = { id: 1, name: 'Admin' } as const;
/**
 * A division with no Purchase Request workflow configured, for the "just submitted" path: the mocked
 * hierarchy's Indirect Materials (templateHierarchy.mock). Direct Materials runs the two-step workflow.
 */
export const MOCK_DIVISION_WITHOUT_WORKFLOW = 112;

let outcome: MockSubmitOutcome = { kind: 'ok' };
/* The rows' ids; a PR number is read from the saved requests (nextPrNumber). */
let nextId = 1;
let nextActionId = 1;
/** Newest first, as the server lists them. */
const stored: TemplateFormSubmissionDetail[] = [];

/** The payloads the mock received, for a test to read back. */
export const mockSubmissions: TemplateFormSubmitRequest[] = [];

/** The number the next Purchase Request will be saved under: the highest saved, plus one -- read, never taken. */
function nextPrNumber(): string {
  const last = stored.reduce((high, row) => {
    const number = /^PR-(\d+)$/.exec(row.referenceNo);
    return number ? Math.max(high, Number(number[1])) : high;
  }, 0);
  return `PR-${String(last + 1).padStart(6, '0')}`;
}

/** As `GET /purchase-requests/next-number`: the same number until a request is saved under it. */
export async function mockReservePurchaseRequestNumber(): Promise<string> {
  return Promise.resolve(nextPrNumber());
}

/** Sets how the next submits answer; call with `{ kind: 'ok' }` to restore. */
export function setMockSubmitOutcome(next: MockSubmitOutcome): void {
  outcome = next;
}

export function resetTemplateFormsMock(): void {
  outcome = { kind: 'ok' };
  nextId = 1;
  nextActionId = 1;
  mockSubmissions.length = 0;
  stored.length = 0;
  /* The cards' "Used N times" counted those requests; they go with them. */
  mockResetTemplateUses();
}

/** Marks a saved request as taken into a BID, as converting its lines to one would (for a test). */
export function mockSetProcessingStatus(
  referenceNo: string,
  processingStatus: 'PARTIAL_PROCESSED' | 'PROCESSED',
  processingStatusLabel: string,
): void {
  const found = stored.find((row) => row.referenceNo === referenceNo);
  if (!found) throw new Error(`No request ${referenceNo}`);
  Object.assign(found, { processingStatus, processingStatusLabel });
}

const now = () => new Date().toISOString().slice(0, 19);

function mockSteps(): SubmissionStep[] {
  return [
    {
      sequence: 1,
      stepName: 'Manager Review',
      approverType: 'REPORTING_MANAGER',
      approverLabel: `${MOCK_USER.name} (reporting manager)`,
      state: 'PENDING',
      pendingStatus: 'Pending Manager Review',
      approvedStatus: 'Manager Approved',
    },
    {
      sequence: 2,
      stepName: 'Final Approval',
      approverType: 'SPECIFIC_USER',
      approverLabel: MOCK_USER.name,
      state: 'WAITING',
      pendingStatus: null,
      approvedStatus: null,
    },
  ];
}

/**
 * Per step: may it be rejected, where does Send Back go, and what the approver is told. Step 1 carries
 * configured messages; step 2 has none, so it answers with the standard sentence, as the server does.
 */
const STEP_RULES: Readonly<
  Record<number, { reject: boolean; referBack: 'INITIATOR' | 'PREVIOUS_STEP'; approveMessage?: string; rejectMessage?: string }>
> = {
  1: {
    reject: true,
    referBack: 'INITIATOR',
    approveMessage: 'Manager review completed',
    rejectMessage: 'Rejected at manager review',
  },
  2: { reject: false, referBack: 'PREVIOUS_STEP' },
};

function pendingStatusOf(step: SubmissionStep): string {
  return step.pendingStatus?.trim() ? step.pendingStatus.trim() : `Pending ${step.stepName}`;
}

function decisionsFor(submission: TemplateFormSubmissionDetail): SubmissionDecision[] {
  /* A deactivated (deleted) request takes no further decision. */
  if (submission.workflowState !== 'IN_PROGRESS' || submission.statusName === 'Inactive') return [];
  const pending = submission.steps.find((step) => step.state === 'PENDING');
  if (!pending) return [];
  const rules = STEP_RULES[pending.sequence];
  return ['APPROVE', ...(rules?.reject ? (['REJECT'] as const) : []), 'SEND_BACK'];
}

function record(submission: TemplateFormSubmissionDetail, entry: Omit<SubmissionHistoryEntry, 'id' | 'actedBy' | 'actedByName' | 'actedDate'>) {
  submission.history.push({
    id: nextActionId++,
    actedBy: MOCK_USER.id,
    actedByName: MOCK_USER.name,
    actedDate: now(),
    ...entry,
  });
}

function withDecisions(submission: TemplateFormSubmissionDetail): TemplateFormSubmissionDetail {
  const copy = structuredClone(submission);
  copy.availableDecisions = decisionsFor(submission);
  copy.approveLabel = submission.steps.find((step) => step.state === 'PENDING')?.sequence === 2 ? 'Sign off' : null;
  return copy;
}

/** The answers with their field snapshots, in form order -- what the server writes as value rows. */
async function snapshotValues(payload: TemplateFormSubmitRequest): Promise<{ name: string; kind: string; values: SubmittedValue[] }> {
  const detail = await getTemplateDetail(payload.templateId);
  const values: SubmittedValue[] = [];
  let ordinal = 0;
  for (const section of renderableSections(detail)) {
    for (const field of section.fields) {
      const answer = payload.values.find(
        (entry) => entry.sectionKey === section.sectionKey && entry.fieldKey === field.fieldKey,
      );
      if (answer) {
        values.push({
          sectionKey: section.sectionKey,
          fieldKey: field.fieldKey,
          fieldLabel: field.label,
          fieldType: field.fieldType,
          ordinal,
          value: answer.value,
        });
      }
      ordinal += 1;
    }
  }
  return { name: detail.name, kind: detail.kind, values };
}

function refusedField(field: string, message: string) {
  return {
    ...createApiError({ message, code: 'VALIDATION_ERROR', status: 400 }),
    details: [{ field, message }] as unknown as Record<string, string[]>,
  };
}

/**
 * The server's Purchase Request hierarchy rule (2026-10-09, reported). Every role names a Business Unit, a Division
 * and a Department -- the Department unless none of the chosen Divisions has one. An Initiator uses only a template
 * they are offered, and at every level it maps sends exactly its values; the levels it leaves unmapped are theirs to
 * choose.
 */
async function checkRequestHierarchy(payload: TemplateFormSubmitRequest, mapped: HierarchyScope): Promise<void> {
  const caller = mockPurchaseRequestCaller();
  if (!caller.admin) {
    if (!mockOfferedToCaller(payload.templateId)) {
      throw refusedField('templateId', 'This template is not available to you');
    }
    for (const level of ['business-units', 'divisions', 'departments'] as const) {
      const field = SCOPE_FIELD[level];
      const want = [...mapped[field]].sort((a, b) => a - b).join(',');
      const sent = [...payload.scope[field]].sort((a, b) => a - b).join(',');
      if (mapped[field].length > 0 && want !== sent) {
        throw refusedField(`scope.${field}`, 'The hierarchy is set by the template and cannot be changed.');
      }
    }
  }
  const errors: Array<{ field: string; message: string }> = [];
  if (payload.scope.businessUnitIds.length === 0) {
    errors.push({ field: 'scope.businessUnitIds', message: 'Please select a Business Unit.' });
  }
  if (payload.scope.divisionIds.length === 0) {
    errors.push({ field: 'scope.divisionIds', message: 'Please select a Division.' });
  }
  if (
    payload.scope.departmentIds.length === 0 &&
    payload.scope.divisionIds.length > 0 &&
    (await mockHierarchyOptions('departments', payload.scope.divisionIds)).length > 0
  ) {
    errors.push({ field: 'scope.departmentIds', message: 'Please select a Department.' });
  }
  if (errors.length > 0) {
    throw {
      ...createApiError({ message: errors.map((error) => error.message).join('; '), code: 'VALIDATION_ERROR', status: 400 }),
      details: errors as unknown as Record<string, string[]>,
    };
  }
}

export async function mockSubmitTemplateForm(payload: TemplateFormSubmitRequest): Promise<TemplateFormSubmissionResult> {
  mockSubmissions.push(payload);

  switch (outcome.kind) {
    case 'invalid':
      throw {
        ...createApiError({ message: 'Please check your input.', code: 'VALIDATION_ERROR', status: 400 }),
        /* The server's own shape: a list, keyed sectionKey.fieldKey. */
        details: outcome.errors as unknown as Record<string, string[]>,
      };
    case 'conflict':
      throw createApiError({
        message: 'This template was changed after the form was opened. Reload it and try again.',
        code: 'UNKNOWN_ERROR',
        status: 409,
      });
    case 'inactive':
      throw createApiError({ message: 'This template is no longer active', code: 'VALIDATION_ERROR', status: 422 });
    case 'forbidden':
      throw createApiError({ message: 'Forbidden', code: 'AUTHORIZATION_ERROR', status: 403 });
    default: {
      const mapped = mockTemplateScope(payload.templateId);
      /* A Purchase Request's hierarchy follows its template and the caller's role (2026-10-09). */
      await checkRequestHierarchy(payload, mapped);
      /* The server's scope rules: a template that applies at every level chosen, and a Division. */
      const refused = HIERARCHY_LEVELS.filter((level) => !scopeApplies(mapped, { ...EMPTY_SCOPE, [SCOPE_FIELD[level]]: payload.scope[SCOPE_FIELD[level]] }));
      const noDivision = payload.scope.divisionIds.length === 0;
      if (noDivision || refused.length > 0) {
        const errors = noDivision
          ? [{ field: 'scope.divisionIds', message: 'Division is required' }]
          : refused.map((level) => ({
              field: `scope.${SCOPE_FIELD[level]}`,
              message: `The template is not available for the selected ${HIERARCHY_LEVEL_LABEL[level]}`,
            }));
        throw {
          ...createApiError({ message: errors.map((error) => error.message).join('; '), code: 'VALIDATION_ERROR', status: 400 }),
          details: errors as unknown as Record<string, string[]>,
        };
      }
      const kindOf = (await getTemplateDetail(payload.templateId)).kind;
      if (!isPurchaseRequestKind(kindOf)) {
        const message = 'Only a PR Template can be submitted as a Purchase Request';
        throw {
          ...createApiError({ message, code: 'VALIDATION_ERROR', status: 400 }),
          details: [{ field: 'templateId', message }] as unknown as Record<string, string[]>,
        };
      }
      /* A Purchase Request's lines, as the server checks them: at least one, each offered, none repeated. */
      const lineErrors: Array<{ field: string; message: string }> = [];
      const lineItems = payload.lineItems ?? [];
      {
        /* A draft may have no line yet (2026-10-26). */
        const asDraft = payload.draft !== undefined ? payload.draft : payload.statusName === 'Inactive';
        if (lineItems.length === 0 && !asDraft) {
          lineErrors.push({ field: 'lineItems', message: 'Add at least one line item.' });
        }
        const seen = new Set<string>();
        /* A line's category is one of its PR Template's (2026-10-09). */
        const ownCategories = mockTemplateCategoryIds(payload.templateId);
        lineItems.forEach((line, index) => {
          if (ownCategories.length > 0 && !ownCategories.includes(line.categoryId)) {
            lineErrors.push({
              field: `lineItems[${String(index)}].categoryId`,
              message: 'This category is not one of the template’s.',
            });
          } else if (!mockLineLabels(line)) {
            lineErrors.push({
              field: `lineItems[${String(index)}].productId`,
              message: 'This product is not offered for the selected location and category.',
            });
          } else if (seen.has(`${String(line.locationId)}:${String(line.productId)}`)) {
            lineErrors.push({
              field: `lineItems[${String(index)}].productId`,
              message: 'This product is already on the request for this location.',
            });
          }
          seen.add(`${String(line.locationId)}:${String(line.productId)}`);
          /* Required By and Remarks are optional; a date is yyyy-MM-dd, remarks at most 500 characters. */
          if (line.requiredBy != null && !/^\d{4}-\d{2}-\d{2}$/.test(line.requiredBy)) {
            lineErrors.push({ field: `lineItems[${String(index)}].requiredBy`, message: 'Required By must be a date.' });
          }
          if (line.remarks != null && line.remarks.length > PR_LINE_REMARKS_MAX) {
            lineErrors.push({
              field: `lineItems[${String(index)}].remarks`,
              message: `Remarks must be at most ${String(PR_LINE_REMARKS_MAX)} characters.`,
            });
          }
        });
        /* A Delivery Date is today or later. */
        const pad = (part: number) => String(part).padStart(2, '0');
        const day = new Date();
        const today = `${String(day.getFullYear())}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
        for (const answer of payload.values) {
          if (answer.fieldKey === 'delivery-date' && typeof answer.value === 'string' && answer.value !== '' && answer.value < today) {
            lineErrors.push({ field: `${answer.sectionKey}.${answer.fieldKey}`, message: 'Delivery Date cannot be in the past.' });
          }
        }
      }
      /* A Purchase Request takes the next PR number -- the one its page showed, unless another request took it. */
      const prNumber = nextPrNumber();
      const shown = payload.referenceNo?.trim() ?? '';
      if (shown !== '' && shown !== prNumber) {
        lineErrors.push({
          field: 'referenceNo',
          message: `${shown} has just been used by another request. This request will be ${prNumber}.`,
        });
      }
      if (lineErrors.length > 0) {
        throw {
          ...createApiError({ message: lineErrors.map((e) => e.message).join('; '), code: 'VALIDATION_ERROR', status: 400 }),
          details: lineErrors as unknown as Record<string, string[]>,
        };
      }
      const scope = mockDescribeScope(payload.scope);
      /* An Initiator's request runs in their own Division, always one of the template's. */
      const caller = mockPurchaseRequestCaller();
      const divisionId =
        !caller.admin && caller.divisionId !== null
          ? caller.divisionId
          : (payload.divisionId ?? payload.scope.divisionIds[0] ?? null);
      const snapshot = await snapshotValues(payload);
      const reference = nextId++;
      const at = now();
      /*
        A draft is numbered and saved, no workflow started. Since design21 (2026-10-22) the body says so itself
        (`draft`) and its Status is the switch's; a body naming no `draft` keeps the older rule -- Inactive is a draft.
      */
      const draft = payload.draft !== undefined ? payload.draft : payload.statusName === 'Inactive';
      const inactive = draft || payload.statusName === 'Inactive';
      const runsWorkflow = !draft && divisionId !== null && divisionId !== MOCK_DIVISION_WITHOUT_WORKFLOW;
      const steps = runsWorkflow ? mockSteps() : [];
      /* The template as the form was filled in from it, kept on the form as the server keeps it (NB-504). */
      const templateNow = structuredClone(await getTemplateDetail(payload.templateId));
      /* A line's own answers, snapshotted against the Line items step's fields as the server does. */
      const lineStep = templateNow.sections.find((section) => section.sectionKey === 'line-items');
      const lineAnswers = (line: (typeof lineItems)[number]): SubmittedValue[] =>
        (line.values ?? []).flatMap((answer) => {
          const field = lineStep?.fields.find((candidate) => candidate.fieldKey === answer.fieldKey);
          return field
            ? [
                {
                  sectionKey: 'line-items',
                  fieldKey: field.fieldKey,
                  fieldLabel: field.label,
                  fieldType: field.fieldType,
                  ordinal: field.ordinal ?? 0,
                  value: answer.value,
                },
              ]
            : [];
        });
      const saved: TemplateFormSubmissionDetail = {
        id: reference,
        referenceNo: prNumber,
        templateId: payload.templateId,
        templateName: snapshot.name,
        templateKind: snapshot.kind,
        templateModifiedDate: payload.templateModifiedDate,
        template: templateNow,
        valueCount: snapshot.values.length,
        divisionId,
        divisionName: scope.divisions.find((node) => node.id === divisionId)?.name ?? null,
        scope,
        workflowState: draft ? 'DRAFT' : runsWorkflow ? 'IN_PROGRESS' : 'NO_WORKFLOW',
        workflowStatus: draft ? 'Draft' : runsWorkflow ? pendingStatusOf(steps[0] as SubmissionStep) : 'Submitted',
        currentStep: runsWorkflow ? 1 : null,
        statusId: inactive ? 12 : 11,
        statusName: inactive ? 'Inactive' : 'Active',
        createdBy: MOCK_USER.id,
        createdByName: MOCK_USER.name,
        createdDate: at,
        modifiedBy: MOCK_USER.id,
        modifiedDate: at,
        values: snapshot.values,
        lineItems: lineItems.map((line, index) => ({
          lineNo: index + 1,
          ...line,
          ...mockLineLabels(line),
          values: lineAnswers(line),
          requiredBy: line.requiredBy ?? null,
          remarks: line.remarks?.trim() ? line.remarks.trim() : null,
          convertedQuantity: 0,
        })),
        /* A Purchase Request's list columns, as the server copies them on submit. */
        requiredBy: (() => {
          const answer = payload.values.find((value) => value.fieldKey === 'delivery-date')?.value;
          return typeof answer === 'string' && answer !== '' ? answer : null;
        })(),
        itemCount: lineItems.length,
        processingStatus: draft ? ('DRAFT' as const) : ('UNPROCESSED' as const),
        processingStatusLabel: draft ? 'Draft' : 'Unprocessed',
        /* design16 (2026-10-19): the Template ID column and the form's Priority, Medium unless chosen. */
        templateCode: templateNow.templateCode ?? null,
        priority: payload.priority ?? 'Medium',
        /* design21: the list's Purchase Request Title, the PR Title answer as the server copies it. */
        title: (() => {
          const answer = payload.values.find((value) => ['pr-title', 'title'].includes(value.fieldKey))?.value;
          return typeof answer === 'string' && answer.trim() !== '' ? answer.trim() : null;
        })(),
        steps,
        history: [],
        availableDecisions: [],
      };
      record(saved, { action: draft ? 'DRAFTED' : 'SUBMITTED', resultStatus: saved.workflowStatus });
      stored.unshift(saved);
      mockRecordTemplateUse(payload.templateId);
      return {
        id: saved.id,
        referenceNo: saved.referenceNo,
        templateId: saved.templateId,
        templateName: saved.templateName,
        templateKind: saved.templateKind,
        statusId: saved.statusId,
        statusName: saved.statusName,
        createdBy: MOCK_USER.id,
        createdDate: at,
        divisionId: saved.divisionId,
        workflowState: saved.workflowState,
        workflowStatus: saved.workflowStatus,
      };
    }
  }
}

const refusedAs = (message: string) =>
  createApiError({ message, code: 'VALIDATION_ERROR', status: 422 });

/**
 * As `PUT /purchase-requests/{id}`: the submit's checks, then the answers, hierarchy and lines saved over the
 * request's under its own number; its workflow kept, and the edit a row of its history. Nothing converted is edited,
 * and the Status its form names -- Active or Inactive -- is written when it differs, so an Inactive request is
 * edited back to Active (or an Active one put down) from the same form.
 */
export async function mockUpdatePurchaseRequest(
  id: number,
  payload: TemplateFormSubmitRequest,
): Promise<TemplateFormSubmissionResult> {
  const found = stored.find((row) => row.id === id && isPurchaseRequestKind(row.templateKind));
  if (!found) {
    throw createApiError({ message: 'Purchase request not found', code: 'NOT_FOUND_ERROR', status: 404 });
  }
  if (found.processingStatus === 'PARTIAL_PROCESSED' || found.processingStatus === 'PROCESSED') {
    throw refusedAs('A purchase request already converted to an RFQ cannot be edited');
  }
  const { referenceNo: _number, statusName: sentStatus, ...body } = payload;
  /* Absent names the record's own status (nothing changes); only the dropdown's two are honoured. */
  const wanted = (sentStatus ?? null) as string | null;
  if (wanted !== null && wanted !== 'Active' && wanted !== 'Inactive') {
    throw createApiError({ message: 'Status must be Active or Inactive', code: 'VALIDATION_ERROR', status: 422 });
  }
  /* The submit's own checks and snapshots, on a scratch row taken back out of the store. */
  const scratch = await mockSubmitTemplateForm(body);
  const index = stored.findIndex((row) => row.id === scratch.id);
  const [edited] = stored.splice(index, 1);
  mockSubmissions.pop();
  if (!edited) throw refusedAs('The purchase request could not be updated');
  const wasStatus = found.statusName;
  Object.assign(found, {
    templateModifiedDate: edited.templateModifiedDate,
    valueCount: edited.valueCount,
    scope: edited.scope,
    values: edited.values,
    lineItems: edited.lineItems,
    requiredBy: edited.requiredBy,
    itemCount: edited.itemCount,
    /* An edit naming no Priority keeps the request's own. */
    priority: payload.priority ?? found.priority ?? 'Medium',
    modifiedDate: now(),
  });
  /*
    A draft: a body naming `draft` (design21) says itself whether it submits -- with the switch's Status; otherwise
    Inactive keeps it one and Active submits it (2026-10-09). Submitted, its workflow starts under the same number.
  */
  if (isDraftState(found.workflowState)) {
    mockSubmissions.unshift(payload);
    const submits = payload.draft !== undefined ? !payload.draft : wanted === 'Active';
    const submittedInactive = payload.draft !== undefined && wanted === 'Inactive';
    if (submits) {
      Object.assign(found, {
        divisionId: edited.divisionId,
        divisionName: edited.divisionName,
        workflowState: edited.workflowState,
        workflowStatus: edited.workflowStatus,
        currentStep: edited.currentStep,
        steps: edited.steps,
        processingStatus: 'UNPROCESSED',
        processingStatusLabel: 'Unprocessed',
        statusId: submittedInactive ? 12 : 11,
        statusName: submittedInactive ? 'Inactive' : 'Active',
      });
      record(found, { action: 'SUBMITTED', resultStatus: found.workflowStatus });
    } else {
      record(found, { action: 'UPDATED', resultStatus: found.workflowStatus });
    }
    return Promise.resolve({
      ...scratch,
      id: found.id,
      referenceNo: found.referenceNo,
      statusId: found.statusId,
      statusName: found.statusName,
      workflowState: found.workflowState,
      workflowStatus: found.workflowStatus,
    });
  }
  if (wanted === 'Inactive') {
    found.statusId = 12;
    found.statusName = 'Inactive';
  } else if (wanted === 'Active') {
    found.statusId = 11;
    found.statusName = 'Active';
  }
  mockSubmissions.unshift(payload);
  record(found, { action: 'UPDATED', resultStatus: found.workflowStatus });
  if (wanted !== null && wasStatus !== found.statusName) {
    record(found, {
      action: found.statusName === 'Inactive' ? 'DEACTIVATED' : 'REACTIVATED',
      resultStatus: found.workflowStatus,
    });
  }
  return Promise.resolve({
    ...scratch,
    id: found.id,
    referenceNo: found.referenceNo,
    workflowState: found.workflowState,
    workflowStatus: found.workflowStatus,
  });
}

/**
 * As `GET /purchase-requests/{id}/delete-impact` (2026-10-13): 404 for a request that is gone, and the platform's 409
 * for one a BID holds. The mock has no BIDs, so a request taken into one -- Partially Processed or Processed -- stands
 * for one held by a single BID.
 */
export function mockPurchaseRequestDeleteImpact(id: number): Promise<void> {
  const found = stored.find((row) => row.id === id && isPurchaseRequestKind(row.templateKind));
  if (!found) {
    return Promise.reject(
      createApiError({ message: 'Purchase request not found', code: 'NOT_FOUND_ERROR', status: 404 }),
    );
  }
  if (found.processingStatus === 'PARTIAL_PROCESSED' || found.processingStatus === 'PROCESSED') {
    return Promise.reject(
      createApiError({
        message:
          'This record cannot be deleted because it is associated with the following records: BID (1). Please '
          + 'remove all associated records or unlink this record from them before attempting to delete it.',
        code: 'UNKNOWN_ERROR',
        status: 409,
        details: { BID: ['1'] },
      }),
    );
  }
  return Promise.resolve();
}

/**
 * As `DELETE /purchase-requests/{id}` (2026-10-09, reported): marked Deleted and gone -- no longer listed, counted or
 * opened (404 by id), a second delete included. The mock drops it from its store, which reads the same from outside.
 */
export async function mockDeactivatePurchaseRequest(id: number): Promise<TemplateFormSubmissionResult> {
  await mockPurchaseRequestDeleteImpact(id);
  const index = stored.findIndex((row) => row.id === id && isPurchaseRequestKind(row.templateKind));
  const found = stored[index];
  if (!found) {
    throw createApiError({ message: 'Purchase request not found', code: 'NOT_FOUND_ERROR', status: 404 });
  }
  stored.splice(index, 1);
  return Promise.resolve({
    id: found.id,
    referenceNo: found.referenceNo,
    templateId: found.templateId,
    templateName: found.templateName,
    templateKind: found.templateKind,
    statusId: 17,
    statusName: 'Deleted',
    createdBy: found.createdBy,
    createdDate: found.createdDate,
    divisionId: found.divisionId,
    workflowState: found.workflowState,
    workflowStatus: found.workflowStatus,
  });
}

/**
 * A Purchase Request against the list's hierarchy (2026-10-09), by its own scope: at every level the filter chooses,
 * the request names one of the values.
 */
function requestMatches(row: TemplateFormSubmissionDetail, filter: HierarchyScope): boolean {
  const own = scopeFromView(row.scope);
  return HIERARCHY_LEVELS.every((level) => {
    const chosen = filter[SCOPE_FIELD[level]];
    return chosen.length === 0 || own[SCOPE_FIELD[level]].some((id) => chosen.includes(id));
  });
}

/** The listing: search over reference, template, status and submitter; paged; newest first. */
export async function mockListSubmissions(query: MasterQuery): Promise<MasterPage<TemplateFormSubmissionRow>> {
  const term = query.search?.trim().toLowerCase() ?? '';
  const awaitingMe = isAwaitingMe(query);
  const filter = scopeOfQuery(query);
  const state = (query as { workflowState?: unknown }).workflowState;
  const processing = (query as { processingStatus?: unknown }).processingStatus;
  const departments = (row: TemplateFormSubmissionDetail) =>
    (row.scope?.departments ?? []).map((node) => node.name.toLowerCase());
  const matching = stored.filter(
    (row) =>
      isPurchaseRequestKind(row.templateKind) &&
      (!state || row.workflowState === state) &&
      (!processing || row.processingStatus === processing) &&
      (!awaitingMe || decisionsFor(row).length > 0) &&
      requestMatches(row, filter) &&
      (query.filters ?? []).every((condition) => conditionHolds(row, condition)) &&
      (term === '' ||
        row.referenceNo.toLowerCase().includes(term) ||
        (row.templateCode ?? '').toLowerCase().includes(term) ||
        row.templateName.toLowerCase().includes(term) ||
        row.workflowStatus.toLowerCase().includes(term) ||
        (row.createdByName ?? '').toLowerCase().includes(term) ||
        (titleOf(row) ?? '').toLowerCase().includes(term) ||
        departments(row).some((name) => name.includes(term))),
  );
  const start = query.page * query.size;
  const rows = matching.slice(start, start + query.size).map((row) => {
    const { values: _values, steps: _steps, history: _history, availableDecisions: _decisions, templateModifiedDate: _modified, scope: _scope, lineItems: _lines, template: _template, ...summary } = row;
    const departmentNames = row.scope?.departments.map((node) => node.name).join(', ') || null;
    return { ...summary, canDecide: decisionsFor(row).length > 0, departmentNames, title: titleOf(row) };
  });
  return Promise.resolve({ rows, total: matching.length });
}

export async function mockGetSubmission(id: number): Promise<TemplateFormSubmissionDetail> {
  const found = stored.find((row) => row.id === id && isPurchaseRequestKind(row.templateKind));
  if (!found) {
    throw createApiError({ message: 'Template form submission not found', code: 'NOT_FOUND_ERROR', status: 404 });
  }
  return Promise.resolve(withDecisions(found));
}

/** A saved Purchase Request by id, or the server's 404. */
function requestOrNotFound(id: number): TemplateFormSubmissionDetail {
  const found = stored.find((row) => row.id === id && isPurchaseRequestKind(row.templateKind));
  if (!found) throw createApiError({ message: 'Purchase request not found', code: 'NOT_FOUND_ERROR', status: 404 });
  return found;
}

/** As `GET /purchase-requests/{id}/usage`: the mock has no BIDs or Purchase Orders. */
export async function mockPurchaseRequestUsage(id: number): Promise<PurchaseRequestUsage> {
  requestOrNotFound(id);
  return Promise.resolve({ bids: 0, purchaseOrders: 0 });
}

/** One decision, by the engine's rules. */
export async function mockDecideSubmission(
  id: number,
  decision: SubmissionDecision,
  comment: string,
  stepSequence?: number | null,
): Promise<SubmissionDecisionResult> {
  const submission = stored.find((row) => row.id === id);
  if (!submission) {
    throw createApiError({ message: 'Template form submission not found', code: 'NOT_FOUND_ERROR', status: 404 });
  }
  if (submission.workflowState === 'IN_PROGRESS' && typeof stepSequence === 'number' && stepSequence !== submission.currentStep) {
    throw createApiError({
      message: `This form has moved on since it was opened: it is now ${submission.workflowStatus}. Reload it to see where it stands.`,
      code: 'UNKNOWN_ERROR',
      status: 409,
    });
  }
  if (!decisionsFor(submission).includes(decision)) {
    throw createApiError({ message: 'This decision is not available on this form', code: 'VALIDATION_ERROR', status: 422 });
  }
  const pending = submission.steps.find((step) => step.state === 'PENDING') as SubmissionStep;
  const note = comment.trim() === '' ? null : comment.trim();
  const before = submission.workflowStatus;
  const rules = STEP_RULES[pending.sequence];
  Object.assign(pending, { actedBy: MOCK_USER.id, actedByName: MOCK_USER.name, actedDate: now(), comment: note });
  const next = submission.steps.find((step) => step.sequence === pending.sequence + 1);
  const previous = submission.steps.find((step) => step.sequence === pending.sequence - 1);

  if (decision === 'APPROVE') {
    pending.state = 'APPROVED';
    if (next) {
      next.state = 'PENDING';
      submission.currentStep = next.sequence;
      submission.workflowStatus = pendingStatusOf(next);
      record(submission, { action: 'APPROVED', stepSequence: pending.sequence, stepName: pending.stepName, resultStatus: submission.workflowStatus, comment: note });
    } else {
      record(submission, { action: 'APPROVED', stepSequence: pending.sequence, stepName: pending.stepName, resultStatus: pending.approvedStatus ?? `${pending.stepName} Approved`, comment: note });
      Object.assign(submission, { workflowState: 'COMPLETED', workflowStatus: 'Completed', currentStep: null });
      record(submission, { action: 'COMPLETED', resultStatus: 'Completed' });
    }
  } else if (decision === 'REJECT') {
    pending.state = 'REJECTED';
    Object.assign(submission, { workflowState: 'REJECTED', workflowStatus: 'Rejected', currentStep: null });
    record(submission, { action: 'REJECTED', stepSequence: pending.sequence, stepName: pending.stepName, resultStatus: 'Rejected', comment: note });
  } else if (STEP_RULES[pending.sequence]?.referBack === 'PREVIOUS_STEP' && previous) {
    pending.state = 'WAITING';
    Object.assign(previous, { state: 'PENDING', actedBy: null, actedByName: null, actedDate: null, comment: null });
    submission.currentStep = previous.sequence;
    submission.workflowStatus = pendingStatusOf(previous);
    record(submission, { action: 'SENT_BACK', stepSequence: pending.sequence, stepName: pending.stepName, resultStatus: submission.workflowStatus, comment: note });
  } else {
    pending.state = 'SENT_BACK';
    Object.assign(submission, { workflowState: 'SENT_BACK', workflowStatus: 'Sent Back', currentStep: null });
    record(submission, { action: 'SENT_BACK', stepSequence: pending.sequence, stepName: pending.stepName, resultStatus: 'Sent Back', comment: note });
  }
  submission.modifiedDate = now();
  const configured = decision === 'APPROVE' ? rules?.approveMessage : decision === 'REJECT' ? rules?.rejectMessage : undefined;
  const updated = withDecisions(submission);
  const message = configured ?? standardDecisionMessage(updated);
  const last = submission.history.filter((entry) => entry.action !== 'COMPLETED').at(-1);
  if (last) Object.assign(last, { previousStatus: before, message });
  return Promise.resolve({ submission: withDecisions(submission), message });
}

/** A request's PR Title answer -- the PR Details section's title field -- as the server's row carries it. */
function titleOf(row: TemplateFormSubmissionDetail): string | null {
  const answer = row.values.find((value) => value.fieldKey === 'pr-title' || value.fieldKey === 'title')?.value;
  return typeof answer === 'string' && answer.trim() !== '' ? answer : null;
}

/** A row's value for one filterable property, as the server's `filter` names them (2026-10-09). */
function filterValueOf(row: TemplateFormSubmissionDetail, property: string): string {
  switch (property) {
    case 'title':
      return titleOf(row) ?? '';
    case 'requestedByName':
      return row.createdByName ?? '';
    case 'requestedOn':
      return (row.createdDate ?? '').slice(0, 10);
    case 'statusName':
      return row.statusName ?? 'Active';
    /* The list's Status column (2026-10-23): Draft for a draft, otherwise its own status -- the server's recordState. */
    case 'recordState':
      return row.workflowState === 'DRAFT' ? 'Draft' : (row.statusName ?? 'Active');
    case 'departmentNames':
      return (row.scope?.departments ?? []).map((node) => node.name).join(', ');
    /* The Processing Status as design16 words it, "Partial Processed" included. */
    case 'processingStatusLabel':
      return row.processingStatus === 'PARTIAL_PROCESSED' ? 'Partially Processed' : (row.processingStatusLabel ?? '');
    default: {
      const raw = (row as unknown as Record<string, unknown>)[property];
      return typeof raw === 'string' ? raw : '';
    }
  }
}

/** One filter-dialog condition on a row, as `MasterSpecs.conditions` applies it (any case). */
function conditionHolds(
  row: TemplateFormSubmissionDetail,
  condition: NonNullable<MasterQuery['filters']>[number],
): boolean {
  const wanted = condition.value.trim().toLowerCase();
  if (condition.column === '' || wanted === '') return true;
  const value = filterValueOf(row, condition.column).toLowerCase();
  switch (condition.comparison) {
    /* A date row: a day against the day the value falls on. */
    case 'is':
      return value === wanted;
    case 'isAfter':
      return value !== '' && value > wanted;
    case 'isBefore':
      return value !== '' && value < wanted;
    case 'equals':
      return value === wanted;
    case 'startsWith':
      return value.startsWith(wanted);
    case 'endsWith':
      return value.endsWith(wanted);
    default:
      return value.includes(wanted);
  }
}

/** The last 30 days against the 30 before, whole percent -- as the server's `changePercent`. */
function changePercent(recent: number, before: number): number {
  if (before === 0) return recent === 0 ? 0 : 100;
  return Math.round(((recent - before) * 100) / before);
}

/** The status cards' counts, over what the list would show (search aside) -- as the server counts them. */
export async function mockSubmissionSummary(query: MasterQuery): Promise<PurchaseRequestSummary> {
  const { rows } = await mockListSubmissions({ ...query, page: 0, size: 10_000, search: '' });
  const day = 24 * 60 * 60 * 1000;
  const nowMs = Date.now();
  const card = (status?: string): PurchaseRequestSummaryCard => {
    const at = rows.filter((row) => !status || row.processingStatus === status);
    const age = (row: TemplateFormSubmissionRow) => nowMs - new Date(row.createdDate ?? 0).getTime();
    return {
      count: at.length,
      changePercent: changePercent(
        at.filter((row) => age(row) < 30 * day).length,
        at.filter((row) => age(row) >= 30 * day && age(row) < 60 * day).length,
      ),
    };
  };
  return {
    total: card(),
    unprocessed: card('UNPROCESSED'),
    processed: card('PROCESSED'),
    partialProcessed: card('PARTIAL_PROCESSED'),
    draft: card('DRAFT'),
  };
}

const EXPORT_COLUMNS: ReadonlyArray<{ key: string; label: string }> = [
  /* design16's columns first, worded as it words them (2026-10-19). */
  { key: 'referenceNo', label: 'Purchase Request Number' },
  { key: 'templateCode', label: 'Template ID' },
  { key: 'title', label: 'PR Title' },
  { key: 'departmentNames', label: 'Department' },
  { key: 'priority', label: 'Priority' },

  { key: 'requiredBy', label: 'Required By' },
  { key: 'itemCount', label: 'Items' },
  { key: 'processingStatusLabel', label: 'Processing Status' },
  { key: 'recordState', label: 'Status' },
  { key: 'templateName', label: 'PR Template' },
  { key: 'divisionName', label: 'Division' },
  { key: 'createdBy', label: 'Requested By' },
  { key: 'createdDate', label: 'Requested On' },
  { key: 'statusName', label: 'Approval Status' },
];

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The export as the server writes it: the view's rows and visible columns, or every row and column. CSV only. */
export async function mockExportSubmissions(request: MasterExportRequest): Promise<Blob> {
  const all = request.scope.rows === 'all';
  const { rows } = await mockListSubmissions(
    all
      ? { page: 0, size: 10_000, ...scopeOfQuery(request.query) }
      : { ...request.query, page: 0, size: 10_000 },
  );
  const columns = EXPORT_COLUMNS.filter((column) => all || request.columns.includes(column.key));
  const value = (row: TemplateFormSubmissionRow, key: string) =>
    key === 'createdBy'
      ? row.createdByName
      : key === 'statusName'
        ? row.workflowStatus
        : key === 'recordState'
          ? row.workflowState === 'DRAFT'
            ? 'Draft'
            : (row.statusName ?? 'Active')
          : (row as never)[key];
  const lines = [
    columns.map((column) => csvCell(column.label)).join(','),
    ...rows.map((row) => columns.map((column) => csvCell(value(row, column.key))).join(',')),
  ];
  return Promise.resolve(new Blob([lines.join('\r\n')], { type: 'text/csv' }));
}

const IMPORT_COLUMNS = [
  'Request Key', 'PR Template', 'Business Unit', 'Division', 'Department', 'PR Title', 'Delivery Date',
  'Description', 'Remark', 'Location', 'Category', 'Product', 'Quantity',
];

/** The import file's layout, with an example request of two lines. */
export function mockImportTemplate(): Blob {
  const rows = [
    IMPORT_COLUMNS,
    ['REQ-1', 'Standard Purchase Request', 'BU-001', 'DIV-001', 'DEPT-001', 'Laptops for new joiners', '', '', '', 'LOC-001', 'CAT-001', 'PRD-001', '2'],
    ['REQ-1', '', '', '', '', '', '', '', '', 'LOC-001', 'CAT-001', 'PRD-002', '1'],
  ];
  return new Blob([rows.map((row) => row.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv' });
}

/** Mock mode reads the file but creates nothing: an import needs the server's masters and workflow. */
export async function mockImportPurchaseRequests(file: File): Promise<MasterImportResult> {
  const lines = (await file.text()).split(/\r?\n/).filter((line) => line.trim() !== '');
  const read = Math.max(0, lines.length - 1);
  return {
    read,
    imported: 0,
    rejected: read === 0 ? [] : [{ line: 2, code: null, reason: 'Importing purchase requests needs the server.' }],
  };
}
